import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Leaf, Droplet, Hospital, Wind, Sparkles, Check, ArrowRight, MapPin,
  Clock, BadgePercent, ShieldCheck, Heart, Users, Quote, Star, ChevronRight
} from "lucide-react";
import { api } from "../lib/api";

const HERO_IMG = "https://images.pexels.com/photos/3997991/pexels-photo-3997991.jpeg?auto=compress&cs=tinysrgb&w=1600";

const GROUPS = [
  {
    key: "drainage",
    label: "Drainage Lymphatique",
    icon: Droplet,
    tagline: "Détox, jambes légères, rétention d'eau",
    description:
      "Un massage manuel doux et rythmé qui relance la circulation lymphatique, élimine les toxines et soulage les sensations de jambes lourdes. Idéal en cure ou en entretien régulier.",
    benefits: [
      "Élimination des toxines & rétention d'eau",
      "Effet jambes légères dès la première séance",
      "Soulagement des œdèmes (grossesse, post-opératoire)",
      "Amélioration de la qualité de la peau",
    ],
  },
  {
    key: "postop",
    label: "Drainage Post-Opératoire",
    icon: Hospital,
    tagline: "Abdominoplastie · Liposuccion · BBL",
    description:
      "Un protocole spécifique adapté aux suites chirurgicales esthétiques. Aide à la cicatrisation, réduit les œdèmes et accélère la récupération en toute douceur.",
    benefits: [
      "Cicatrisation accompagnée & sécurisée",
      "Réduction des œdèmes et ecchymoses",
      "Limitation des fibroses & adhérences",
      "Suivi personnalisé post-opératoire",
    ],
  },
  {
    key: "drainsculpt",
    label: "Drain & Sculpt",
    icon: Wind,
    tagline: "Combo Drainage + Madérothérapie",
    description:
      "Une signature exclusive qui combine drainage lymphatique et madérothérapie (massage avec ustensiles en bois) pour un effet sculptant et anti-cellulite immédiatement visible.",
    benefits: [
      "Action anti-cellulite ciblée",
      "Affinement & sculpting visible dès la 3ᵉ séance",
      "Tonification & raffermissement de la peau",
      "Idéal avant un événement (mariage, vacances)",
    ],
  },
];

const PROCESS = [
  { num: "01", title: "Bilan initial", text: "Échange téléphonique pour comprendre vos besoins & objectifs (10 min)." },
  { num: "02", title: "Rendez-vous à domicile", text: "Rosalie se déplace avec son matériel — vous n'avez rien à prévoir." },
  { num: "03", title: "Soin sur-mesure", text: "Protocole adapté à votre morphologie & sensibilité, dans le calme de votre intérieur." },
  { num: "04", title: "Conseils & suivi", text: "Recommandations hygiène de vie, hydratation et programme personnalisé." },
];

const FAQ = [
  {
    q: "Combien de séances pour voir un résultat ?",
    a: "Les premiers effets (jambes légères, peau plus lumineuse) sont sensibles dès la première séance. Pour un résultat durable, on recommande une cure de 5 séances espacées d'une semaine, suivie d'un entretien mensuel.",
  },
  {
    q: "Le crédit d'impôt 50% s'applique-t-il aux massages ?",
    a: "Oui — FORM'ELEC est agréée Services à la Personne. Tous les soins à domicile bénéficient du crédit d'impôt de 50% via l'avance immédiate URSSAF. Vous ne payez que 50% du montant directement.",
  },
  {
    q: "Y a-t-il des contre-indications ?",
    a: "Le drainage lymphatique est déconseillé en cas de thrombose veineuse, infection aiguë, cancer en cours de traitement ou insuffisance cardiaque sévère. Un échange avant la première séance permet de vérifier votre éligibilité.",
  },
  {
    q: "Combien de temps dure une séance ?",
    a: "Entre 45 minutes (ventre + jambes, Drain & Sculpt haut du corps) et 1h30 (Drain & Sculpt corps entier). Comptez 15 minutes supplémentaires pour l'installation & l'échange.",
  },
];

export default function UniversMassages() {
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    api.get("/packs").then((r) => setPacks(r.data.filter((p) => p.category === "massage"))).catch(() => {});
  }, []);

  const packsByGroup = useMemo(() => {
    const map = {};
    packs.forEach((p) => {
      const k = p.subcategory || "other";
      if (!map[k]) map[k] = [];
      map[k].push(p);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.nb_seances - b.nb_seances || a.price - b.price));
    return map;
  }, [packs]);

  return (
    <div className="bg-[#FBF1E6]" data-testid="page-univers-massages">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F2E0CC] via-[#E6CBAE] to-[#C49B7C]/30 pt-20 pb-28 px-6 lg:px-10">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#5A3E27]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/60 backdrop-blur-sm text-[#5A3E27] text-xs font-bold uppercase tracking-[0.2em] mb-6">
              <Leaf className="w-3.5 h-3.5" /> Univers 02 — Massages
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#5A3E27] leading-tight">
              Massages &amp; <span className="font-light italic">Bien-être</span>
            </h1>
            <p className="text-lg lg:text-xl text-[#8B6644] mt-5 leading-relaxed">
              Drainer · Sculpter · Apaiser — des soins manuels personnalisés,
              dispensés à domicile par Rosalie.
            </p>
            <p className="text-sm text-[#5A3E27]/80 mt-4 max-w-xl leading-relaxed">
              Trois familles de soins complémentaires pour répondre à chaque besoin :
              drainage lymphatique détox, accompagnement post-opératoire et combos
              Drain &amp; Sculpt pour un effet sculptant durable.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/reservation"
                className="inline-flex items-center gap-2 bg-[#5A3E27] text-white font-bold px-7 py-3.5 rounded-full hover:bg-[#3D2A1A] transition-all shadow-lg"
                data-testid="hero-massage-reserve"
              >
                Réserver mon soin <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#prestations"
                className="inline-flex items-center gap-2 border-2 border-[#5A3E27]/40 text-[#5A3E27] font-bold px-7 py-3.5 rounded-full hover:bg-white/40 transition-all"
              >
                Voir les prestations <ChevronRight className="w-4 h-4" />
              </a>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-4 mt-10 pt-8 border-t border-[#5A3E27]/20">
              <TrustBadge icon={BadgePercent} title="−50%" subtitle="Crédit d'impôt" />
              <TrustBadge icon={MapPin} title="À domicile" subtitle="Istres &amp; alentours" />
              <TrustBadge icon={ShieldCheck} title="Agréé SAP" subtitle="Services à la personne" />
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
              <img src={HERO_IMG} alt="Massages bien-être à domicile" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl max-w-[240px] hidden sm:block">
              <div className="flex items-center gap-2 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-[#C49B7C] text-[#C49B7C]" />
                ))}
              </div>
              <p className="text-xs text-fegray-dark italic leading-relaxed">
                "Les mains de Rosalie sont magiques — mes jambes sont enfin légères !"
              </p>
              <p className="text-[10px] text-fegray mt-2 font-semibold uppercase tracking-wider">— Sophie M.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-16 lg:py-20 px-6 lg:px-10 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#C49B7C] font-bold uppercase tracking-[0.25em] text-xs mb-3">— Ma philosophie</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-fegray-dark mb-6">
            Des soins <span className="text-[#C49B7C]">sur-mesure</span>, chez vous, en toute intimité
          </h2>
          <p className="text-fegray text-base lg:text-lg leading-relaxed">
            Chaque corps est unique, chaque attente l'est aussi. Je viens chez vous avec
            mon matériel, mes huiles et mon écoute. Le soin s'adapte à votre morphologie,
            vos zones de tension, vos objectifs — jamais l'inverse. C'est cette approche
            personnalisée qui fait la différence et qui amène mes clientes à revenir.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
            {[
              { icon: Heart, label: "Approche manuelle exclusive — pas d'appareillage agressif" },
              { icon: Clock, label: "Créneaux flexibles — soir & week-end disponibles" },
              { icon: Users, label: "Suivi long terme — plans personnalisés et cures progressives" },
            ].map((it, i) => {
              const Ic = it.icon;
              return (
                <div key={i} className="text-left bg-[#FBF1E6] rounded-2xl p-6">
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mb-4 shadow-sm">
                    <Ic className="w-6 h-6 text-[#C49B7C]" strokeWidth={1.8} />
                  </div>
                  <p className="text-sm text-fegray-dark font-medium leading-relaxed">{it.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRESTATIONS — GROUPES */}
      <section id="prestations" className="py-16 lg:py-24 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#C49B7C] font-bold uppercase tracking-[0.25em] text-xs mb-3">— Mes prestations</p>
            <h2 className="text-3xl lg:text-5xl font-bold text-fegray-dark">
              Trois familles, <span className="text-[#C49B7C]">une expertise</span>
            </h2>
            <p className="text-fegray mt-4 max-w-xl mx-auto">
              Découvrez chaque univers en détail — chaque prestation se réserve directement.
            </p>
          </div>

          <div className="space-y-16">
            {GROUPS.map((g, gi) => {
              const Icon = g.icon;
              const groupPacks = packsByGroup[g.key] || [];
              return (
                <div key={g.key} data-testid={`group-${g.key}`} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                  {/* Group description */}
                  <div className="lg:col-span-4">
                    <div className="sticky top-32">
                      <div className="w-16 h-16 rounded-2xl bg-[#C49B7C] flex items-center justify-center shadow-lg mb-5">
                        <Icon className="w-8 h-8 text-white" strokeWidth={1.8} />
                      </div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#8B6644] font-bold mb-2">
                        Famille 0{gi + 1}
                      </p>
                      <h3 className="text-2xl lg:text-3xl font-bold text-fegray-dark mb-3">{g.label}</h3>
                      <p className="text-sm font-semibold text-[#C49B7C] mb-4">{g.tagline}</p>
                      <p className="text-sm text-fegray leading-relaxed mb-6">{g.description}</p>
                      <ul className="space-y-2.5">
                        {g.benefits.map((b, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-fegray-dark">
                            <Check className="w-4 h-4 mt-0.5 text-[#C49B7C] flex-shrink-0" strokeWidth={2.5} />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Packs in this group */}
                  <div className="lg:col-span-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {groupPacks.map((p) => (
                        <div
                          key={p.id}
                          data-testid={`pack-${p.id}`}
                          className={`rounded-2xl p-6 flex flex-col bg-white border-2 transition-all hover:shadow-lg hover:-translate-y-0.5 ${
                            p.popular ? "border-[#C49B7C] ring-2 ring-[#C49B7C]/20" : "border-gray-100"
                          }`}
                        >
                          {p.popular && (
                            <span className="inline-flex self-start items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#C49B7C] text-white px-3 py-1 rounded-full mb-3">
                              <Sparkles className="w-3 h-3" /> Populaire
                            </span>
                          )}
                          <h4 className="text-lg font-bold text-fegray-dark leading-tight">{p.name}</h4>
                          <p className="text-xs text-fegray mt-1.5 mb-4 leading-relaxed flex-1">{p.description}</p>

                          <div className="pt-4 border-t border-gray-100 flex items-baseline justify-between mb-4">
                            <div>
                              <div className="text-2xl font-extrabold text-[#C49B7C]">{p.price}€</div>
                              <div className="text-[10px] text-fegray uppercase tracking-wider">
                                soit {(p.price * 0.5).toFixed(2)}€ après crédit d'impôt
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-[10px] text-fegray uppercase tracking-wider">
                                {p.nb_seances} séance{p.nb_seances > 1 ? "s" : ""}
                              </div>
                            </div>
                          </div>

                          <Link
                            to={`/reservation?pack=${p.id}`}
                            data-testid={`pack-reserve-${p.id}`}
                            className={`mt-auto inline-flex items-center justify-center gap-2 w-full py-2.5 rounded-full text-sm font-bold transition-all ${
                              p.popular
                                ? "bg-[#C49B7C] text-white hover:bg-[#A8825F]"
                                : "border-2 border-[#C49B7C] text-[#8B6644] hover:bg-[#C49B7C] hover:text-white"
                            }`}
                          >
                            Réserver <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 lg:py-20 px-6 lg:px-10 bg-[#F2E0CC]/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#C49B7C] font-bold uppercase tracking-[0.25em] text-xs mb-3">— Comment ça se passe ?</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-fegray-dark">
              Votre séance en <span className="text-[#C49B7C]">4 étapes</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map((step, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 relative">
                <div className="text-5xl font-extrabold text-[#C49B7C]/20 absolute top-4 right-5 leading-none">{step.num}</div>
                <h3 className="font-bold text-fegray-dark text-lg mb-2 relative">{step.title}</h3>
                <p className="text-xs text-fegray leading-relaxed relative">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20 px-6 lg:px-10 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[#C49B7C] font-bold uppercase tracking-[0.25em] text-xs mb-3">— Questions fréquentes</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-fegray-dark">Tout ce que vous voulez savoir</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <details
                key={i}
                data-testid={`massage-faq-${i}`}
                className="group rounded-2xl border border-gray-100 bg-[#FBF1E6]/40 p-5 cursor-pointer transition-all hover:border-[#C49B7C]/40 open:bg-white open:shadow-md"
              >
                <summary className="font-bold text-fegray-dark text-sm sm:text-base flex justify-between items-start gap-4 list-none">
                  <span>{item.q}</span>
                  <span className="text-[#C49B7C] text-xl group-open:rotate-45 transition-transform shrink-0">+</span>
                </summary>
                <p className="text-sm text-fegray leading-relaxed mt-3">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 lg:py-20 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-br from-[#5A3E27] via-[#8B6644] to-[#C49B7C] p-10 lg:p-14 text-center relative">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
          <div className="relative">
            <Quote className="w-10 h-10 text-white/40 mx-auto mb-4" />
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Prête à vous offrir un moment <em className="font-light">pour vous</em> ?
            </h2>
            <p className="text-white/85 max-w-xl mx-auto mb-8">
              Réservez votre soin en quelques clics, à la date et l'heure qui vous arrangent.
              Crédit d'impôt 50% appliqué immédiatement.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/reservation"
                className="inline-flex items-center gap-2 bg-white text-[#5A3E27] font-bold px-7 py-3.5 rounded-full hover:bg-[#FBF1E6] transition-all shadow-lg"
                data-testid="bottom-cta-reserve"
              >
                Réserver mon soin <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border-2 border-white/40 text-white font-bold px-7 py-3.5 rounded-full hover:bg-white/10 transition-all"
              >
                Une question ? Me contacter
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function TrustBadge({ icon: Icon, title, subtitle }) {
  return (
    <div className="text-center">
      <Icon className="w-5 h-5 text-[#5A3E27] mx-auto mb-1.5" strokeWidth={1.8} />
      <div className="text-sm font-bold text-[#5A3E27]" dangerouslySetInnerHTML={{ __html: title }} />
      <div className="text-[10px] text-[#8B6644] uppercase tracking-wider mt-0.5" dangerouslySetInnerHTML={{ __html: subtitle }} />
    </div>
  );
}
